import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  TicketIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import apiService, { Coupon, CouponStats, CouponFilters, CouponFormData } from '../../services/api';
import CouponModal from '../../components/admin/CouponModal';
import ConfirmModal from '../../components/admin/ConfirmModal';
import CouponDetailModal from '../../components/admin/CouponDetailModal';


const AdminCoupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [stats, setStats] = useState<CouponStats>({ total: 0, active: 0, expired: 0, used: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<CouponFilters>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const [couponsData, statsData] = await Promise.all([
        apiService.getCoupons(filters),
        apiService.getCouponStats()
      ]);
      setCoupons(couponsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading coupons:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      scheduled: 'bg-blue-100 text-blue-800',
      used_up: 'bg-yellow-100 text-yellow-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleOpenModal = (coupon: Coupon | null) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetailModalOpen(false);
    setSelectedCoupon(null);
  };

  const handleDeleteCoupon = (coupon: Coupon) => {
    setConfirmMessage(`Are you sure you want to delete "${coupon.code}"? This action cannot be undone.`);
    setConfirmAction(() => () => deleteCoupon(coupon.id));
    setShowConfirmModal(true);
  };

  const handleSaveCoupon = async (couponData: CouponFormData) => {
    try {
      if (selectedCoupon) {
        await apiService.updateCoupon(selectedCoupon.id, couponData);
      } else {
        await apiService.createCoupon(couponData);
      }
      handleCloseModal();
      loadCoupons(); // Reload coupons after saving
    } catch (error) {
      console.error('Error saving coupon:', error);
      // You might want to show an error notification to the user
    }
  };

  const deleteCoupon = async (couponId: number) => {
    try {
      await apiService.deleteCoupon(couponId);
      loadCoupons(); // Reload coupons after deleting
    } catch (error) {
      console.error('Error deleting coupon:', error);
      // You might want to show an error notification to the user
    }
  };

  const filteredCount = coupons.length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Coupon Management</h1>
            <p className="text-gray-600 mt-1">Manage discount coupons and promotional codes</p>
          </div>
          <button 
            onClick={() => handleOpenModal(null)}
            className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Coupon</span>
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TicketIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Coupons</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <TicketIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <TicketIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Expired</p>
                <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TicketIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Used</p>
                <p className="text-2xl font-bold text-gray-900">{stats.used}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search coupons..."
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
              />
            </div>

            <select
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
              <option value="scheduled">Scheduled</option>
            </select>

            <select
              value={filters.discount_type || ''}
              onChange={(e) => setFilters({ ...filters, discount_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
            >
              <option value="">All Types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed_amount">Fixed Amount</option>
            </select>

            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
            >
              Clear Filters
              {filteredCount < stats.total && (
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredCount} of {stats.total})
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <TicketIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No coupons found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first coupon.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{coupon.code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coupon.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coupon.computed_discount_display}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(coupon.computed_status || 'active')}`}>
                        {coupon.computed_status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coupon.used_count}{coupon.usage_limit ? `/${coupon.usage_limit}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => {
                          setSelectedCoupon(coupon);
                          setIsDetailModalOpen(true);
                        }} className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleOpenModal(coupon)} className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteCoupon(coupon)} className="text-red-600 hover:text-red-900 p-1 rounded transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <CouponModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveCoupon}
          coupon={selectedCoupon}
        />
      )}

      {/* View Detail Modal */}
      {isDetailModalOpen && (
        <CouponDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseModal}
          coupon={selectedCoupon}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={() => {
            confirmAction();
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
          type="danger"
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default AdminCoupons;
